// The catalogue 1.0 maps (city_os#83, telcofy-demo#103), read through the data API: /maps/list lists each country's
// catalogue layers (rows with a geo_type), /maps reads a layer in a box, /maps/by-ids reads named areas. The layers are
// maps.<country>_admin_areas (admin_lvl_<n>, country, station), _model_grid (every cell of the 250 m to 10 km grid,
// with its size and parents) and _network_links (road links). Answers in the shapes of mapsApi, so the picker and the
// shape loader need no second path. A backend that lists no catalogue layers for a country (production before the
// cut-over) leaves the old maps in place.
import { DATA_API_BASE_URL } from '../config/api';
import {
  MODEL_GRID,
  NETWORK_LINKS,
  PICKER_SKIPPED_GEO_TYPES,
  catalogueGeoTypeOrder,
  gridCellKey,
  modelGridRung,
} from '../config/catalogue';
import type { CountryCode, MapType } from '../config/countries';
import { getJson } from './http';
import type { AvailableMapsResponse, BoundingBox, MapGeometry, MapsApiResponse } from './mapsApi';

/** The prefix of the picker's layer names for a catalogue geo type (see mapLayerGeoType). */
export const CATALOGUE_LAYER_PREFIX = 'catalogue:';

/** The model grid's cell sizes, finest first: one grid, so every size is a rung of it. */
export const MODEL_GRID_SIZES: readonly string[] = ['grid_250m', 'grid_500m', 'grid_1000m', 'grid_5000m', 'grid_10000m'];

/** The most areas one drawing shows; a box holding more asks the user to zoom in. */
const TILE_LIMIT = 6000;
/** The most ids one by-ids request names, so the URL stays well under a server's limit. */
const IDS_PER_REQUEST = 150;

interface ListRow {
  table_name?: string;
  description?: string;
  geo_type?: string;
}

const listCache = new Map<CountryCode, Promise<string[]>>();

/** The catalogue geo types /maps/list names for the country, in picker order; [] when it names none. */
export function catalogueGeoTypes(c: CountryCode): Promise<string[]> {
  let found = listCache.get(c);
  if (!found) {
    found = getJson<{ results?: ListRow[] } | null>(DATA_API_BASE_URL, '/maps/list', { query: { country_code: c } }).then((res) =>
      Array.from(new Set((res?.results ?? []).flatMap((r) => (typeof r.geo_type === 'string' && r.geo_type ? [r.geo_type] : []))))
        .sort((a, b) => catalogueGeoTypeOrder(a) - catalogueGeoTypeOrder(b)),
    );
    const mine = found;
    // A failure is not kept: the next call asks again.
    mine.catch(() => {
      if (listCache.get(c) === mine) listCache.delete(c);
    });
    listCache.set(c, mine);
  }
  return found;
}

/** True when the data API lists catalogue maps for the country, so new areas there are made on them. */
export async function catalogueServesMaps(c: CountryCode): Promise<boolean> {
  return (await catalogueGeoTypes(c)).some((g) => g === MODEL_GRID || /^admin_lvl_\d+$/.test(g));
}

/** True when the data API lists the catalogue's road links for the country. */
export async function catalogueServesRoadLinks(c: CountryCode): Promise<boolean> {
  return (await catalogueGeoTypes(c)).includes(NETWORK_LINKS);
}

/** The layers the area picker offers for a country, as /maps/list rows named `catalogue:<geo_type>`. */
export async function getCatalogueAvailableMaps(c: CountryCode): Promise<AvailableMapsResponse> {
  const types = (await catalogueGeoTypes(c)).filter((g) => !PICKER_SKIPPED_GEO_TYPES.includes(g) && g !== NETWORK_LINKS);
  return {
    columns: ['table_name', 'description'],
    country_code: c,
    results: types.map((g) => ({
      table_name: `${CATALOGUE_LAYER_PREFIX}${g}`,
      description:
        g === MODEL_GRID
          ? 'One grid from 10 km down to 250 m: zoom in for finer cells, which split where trips are dense.'
          : 'Catalogue 1.0',
    })),
  };
}

interface MapRow {
  geo_id: string | number;
  geo_type?: string;
  name?: string | null;
  size_m?: number | string | null;
  parent_500m?: string | null;
  parent_1km?: string | null;
  parent_5km?: string | null;
  parent_10km?: string | null;
  geometry: MapGeometry['geometry'];
}

const sizeOf = (r: MapRow): number | null => {
  const n = Number(r.size_m);
  return r.size_m != null && Number.isFinite(n) ? n : null;
};

const toGeometry = (r: MapRow): MapGeometry => {
  const size = sizeOf(r);
  return {
    geo_id: String(r.geo_id),
    geometry: r.geometry,
    ...(r.name != null ? { name: r.name } : {}),
    ...(size !== null ? { size_m: size } : {}),
  };
};

const PARENT_COLUMNS = [
  ['parent_500m', 'grid_500m'],
  ['parent_1km', 'grid_1000m'],
  ['parent_5km', 'grid_5000m'],
  ['parent_10km', 'grid_10000m'],
] as const;

/**
 * A model-grid row as a picker cell: its key carries its size ('grid_500m:<id>': an SSB square and its south-west
 * children share a number), and its parents' keys come with it.
 */
const toGridCell = (r: MapRow): MapGeometry => ({
  ...toGeometry(r),
  geo_id: gridCellKey(String(r.geo_type), String(r.geo_id)),
  parents: PARENT_COLUMNS.flatMap(([col, type]) => (r[col] ? [gridCellKey(type, String(r[col]))] : [])),
});

const responseOf = (type: MapType, bbox: BoundingBox, results: MapGeometry[], truncated = false): MapsApiResponse => ({
  type,
  bbox: { lng1: String(bbox.lng1), lat1: String(bbox.lat1), lng2: String(bbox.lng2), lat2: String(bbox.lat2) },
  results,
  ...(truncated ? { truncated: true } : {}),
});

const NO_BOX: BoundingBox = { lng1: 0, lat1: 0, lng2: 0, lat2: 0 };

/**
 * The finest cells of the adaptive grid among `rows`, for picking ground on the map: a square is split when a
 * smaller row names it as its parent. Children lie inside their parent, so a split square that meets a box always
 * has a child in it too. Geometry only: the products are nested totals, not a partition.
 */
export function modelGridLeaves<T extends Pick<MapRow, 'geo_id' | 'geo_type' | 'parent_500m' | 'parent_1km'>>(rows: T[]): T[] {
  const split = new Set<string>();
  for (const r of rows) {
    if (r.parent_500m) split.add(`grid_500m|${r.parent_500m}`);
    if (r.parent_1km && r.geo_type !== 'grid_1000m') split.add(`grid_1000m|${r.parent_1km}`);
  }
  return rows.filter((r) => !split.has(`${r.geo_type}|${r.geo_id}`));
}

async function boxRows(type: string, bbox: BoundingBox, c: CountryCode): Promise<MapRow[]> {
  const res = await getJson<{ results?: MapRow[] } | null>(DATA_API_BASE_URL, '/maps', {
    query: {
      geo_type: type,
      lng1: bbox.lng1.toFixed(5),
      lat1: bbox.lat1.toFixed(5),
      lng2: bbox.lng2.toFixed(5),
      lat2: bbox.lat2.toFixed(5),
      country_code: c,
    },
  });
  return res?.results ?? [];
}

/**
 * The areas of one catalogue geo type that meet the box. The model grid draws the rung that suits the zoom: one
 * coarse size, or the adaptive cells, finest first. Too many for one drawing gives an empty, truncated answer.
 */
export async function getCatalogueMapData(type: MapType, bbox: BoundingBox, c: CountryCode, zoom = 12): Promise<MapsApiResponse> {
  const found = await boxRows(type, bbox, c);
  if (type !== MODEL_GRID) {
    if (found.length > TILE_LIMIT) return responseOf(type, bbox, [], true);
    return responseOf(type, bbox, found.map(toGeometry));
  }
  const rung = modelGridRung(zoom, MODEL_GRID_SIZES);
  const onRung = found.filter((r) => rung.geoTypes.includes(String(r.geo_type)));
  const cells = rung.adaptive ? modelGridLeaves(onRung) : onRung;
  // Never a partial map: the picker shows "zoom in" instead.
  if (cells.length > TILE_LIMIT) return responseOf(type, bbox, [], true);
  return responseOf(type, bbox, cells.map(toGridCell));
}

async function byIds(type: string, ids: readonly string[], c: CountryCode): Promise<MapRow[]> {
  const requests: Array<Promise<MapRow[]>> = [];
  for (let i = 0; i < ids.length; i += IDS_PER_REQUEST) {
    const chunk = ids.slice(i, i + IDS_PER_REQUEST);
    requests.push(
      getJson<{ results?: MapRow[] } | null>(DATA_API_BASE_URL, '/maps/by-ids', {
        query: { geo_type: type, geo_ids: chunk.join(','), country_code: c },
      }).then((res) => res?.results ?? []),
    );
  }
  return (await Promise.all(requests)).flat();
}

/**
 * The shapes of named areas of one catalogue geo type. Model-grid ids are cell keys ('grid_500m:<id>'), which
 * /maps/by-ids reads as one size each. Road links are link ids (whole numbers); anything else is left out.
 */
export async function getCatalogueGeosByIds(type: MapType, geoIds: string[], c: CountryCode): Promise<MapsApiResponse> {
  const ids = Array.from(new Set(geoIds.map((id) => String(id).trim()).filter(Boolean)));
  if (type === NETWORK_LINKS) {
    const links = ids.filter((id) => /^\d{1,19}$/.test(id));
    return responseOf(type, NO_BOX, (await byIds(type, links, c)).map(toGeometry));
  }
  if (type === MODEL_GRID) {
    const cells = ids.filter((id) => /^grid_\d+m:/.test(id));
    return responseOf(type, NO_BOX, (await byIds(type, cells, c)).map(toGridCell));
  }
  return responseOf(type, NO_BOX, (await byIds(type, ids, c)).map(toGeometry));
}

/** The road links that meet the box; too many for one drawing gives an empty, truncated answer. */
export function getCatalogueLinks(bbox: BoundingBox, c: CountryCode): Promise<MapsApiResponse> {
  return getCatalogueMapData(NETWORK_LINKS as MapType, bbox, c);
}
